/**
 * Issue: #5997
 *
 * AskAIModal — RAG 質問応答モーダル.
 * 1) /v1/ask または /v1/ask/stream で回答を取得
 * 2) 回答本文 + 引用元 (citation) リストを表示
 * 3) 引用元クリックで onJump (= 該当 message へジャンプ)
 *
 * SSE ストリーミング対応 (デフォルト ON). 中断は Esc または abort ボタン.
 */
import { useCallback, useEffect, useState } from "react";
import { askAi, askAiStream } from "./api";
import { Modal } from "@/components/ui/Modal";
import type { RagAnswer, RagCitation } from "./types";

export interface AskAIModalProps {
  open: boolean;
  question: string;
  onClose: () => void;
  onJumpCitation?: (cite: RagCitation) => void;
  /** SSE を使うか (default true) */
  stream?: boolean;
  channelIds?: string[];
  authorIds?: string[];
  hasFile?: boolean;
  dateRange?: { before?: number; after?: number };
  roomNames?: Record<string, string>;
}

export function AskAIModal(props: AskAIModalProps): JSX.Element | null {
  const { open, question, onClose, onJumpCitation, stream = true, channelIds, authorIds, hasFile, dateRange, roomNames = {} } = props;
  const askBody = { query: question, channel_ids: channelIds, author_ids: authorIds, has_file: hasFile, date_range: dateRange, top_k: 8 };
  const [partial, setPartial] = useState<string>("");
  const [answer, setAnswer] = useState<RagAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [aborter, setAborter] = useState<{ abort: () => void } | null>(null);

  const handleClose = useCallback(() => {
    aborter?.abort();
    setAborter(null);
    onClose();
  }, [aborter, onClose]);

  useEffect(() => {
    if (!open || !question.trim()) return;
    setPartial("");
    setAnswer(null);
    setError(null);
    setLoading(true);

    if (stream) {
      const a = askAiStream(
        askBody,
        {
          onDelta: (text) => setPartial((prev) => prev + text),
          onDone: (final) => {
            setAnswer({
              answer: final.answer,
              citations: final.citations,
              confidence: final.confidence,
              model: final.model || "",
              region: final.region || "",
              prompt_tokens: 0,
              completion_tokens: 0,
              context_count: final.context_count ?? final.citations.length,
            });
            setLoading(false);
          },
          onError: (err) => {
            setError(err.message);
            setLoading(false);
          },
        },
      );
      setAborter(a);
      return () => {
        a.abort();
      };
    } else {
      let cancelled = false;
      askAi(askBody)
        .then((r) => {
          if (cancelled) return;
          setAnswer(r);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [open, question, stream, channelIds]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  if (!open) return null;

  const text = answer?.answer || partial || (loading ? "考え中…" : "");
  return (
    <Modal open={open} onClose={handleClose} title="AI 検索回答" size="lg">
      <div data-testid="ask-ai-modal">
      <div className="ask-ai-modal__panel">
        <header className="ask-ai-modal__header">
          <h2 className="ask-ai-modal__title">AI 回答</h2>
          <button
            type="button"
            className="ask-ai-modal__close"
            onClick={handleClose}
            aria-label="閉じる"
            data-testid="ask-ai-modal-close"
          >
            ×
          </button>
        </header>

        <section className="ask-ai-modal__question" data-testid="ask-ai-modal-question">
          <strong>質問:</strong> <span>{question}</span>
        </section>

        <section className="ask-ai-modal__answer" data-testid="ask-ai-modal-answer" aria-live="polite">
          {error ? (
            <p className="ask-ai-modal__error" role="alert">
              エラー: {error}
            </p>
          ) : (
            <p className="ask-ai-modal__text">{text}</p>
          )}
          {answer ? (
            <div className="ask-ai-modal__meta">
              <span data-testid="ask-ai-modal-confidence">確信度: {(answer.confidence * 100).toFixed(0)}%</span>
              {answer.model && <span>モデル: {answer.model}</span>}
              <span>引用 {answer.citations.length} 件</span>
            </div>
          ) : null}
        </section>

        {answer && answer.citations.length > 0 ? (
          <section className="ask-ai-modal__citations" aria-label="引用元">
            <h3>引用元</h3>
            <ul data-testid="ask-ai-modal-citations">
              {answer.citations.map((c, i) => (
                <li key={c.message_id} className="ask-ai-modal__citation">
                  <button
                    type="button"
                    onClick={() => onJumpCitation?.(c)}
                    data-testid="ask-ai-modal-citation"
                    data-message-id={c.message_id}
                  >
                    <span className="ask-ai-modal__citation-num">[{i + 1}]</span>
                    <span className="ask-ai-modal__citation-room">{roomNames[c.room_id] || c.room_id}</span>
                    <time>{new Date(c.created_at).toLocaleString("ja-JP")}</time>
                    <p>{stripMark(c.excerpt)}</p>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
    </Modal>
  );
}

function stripMark(s: string): string {
  return s.replace(/<\/?mark>/g, "");
}
